<template>
  <!-- Force update: full-screen blocking overlay -->
  <v-dialog v-model="showForceUpdate" persistent :scrim="true" max-width="380">
    <div class="update-card">
      <div class="update-card__icon update-card__icon--required">
        <v-icon size="32" color="white">{{ mdiUpdate }}</v-icon>
      </div>
      <div class="update-card__title">{{ $t('update_required') }}</div>
      <div class="update-card__desc">{{ message || $t('update_required_desc') }}</div>
      <div class="update-card__versions">
        {{ version }} <span class="update-card__arrow">→</span> {{ latestVersion }}
      </div>
      <button class="update-card__btn update-card__btn--primary" @click="goToStore">
        {{ $t('update_now') }}
      </button>
    </div>
  </v-dialog>

  <!-- Update available: dismissible bottom sheet -->
  <v-bottom-sheet v-model="showUpdateAvailable" :persistent="false">
    <div class="update-sheet">
      <div class="update-sheet__handle" />
      <div class="update-sheet__icon">
        <v-icon size="28" color="white">{{ mdiUpdate }}</v-icon>
      </div>
      <div class="update-card__title">{{ $t('update_available') }}</div>
      <div class="update-card__desc">{{ message || $t('update_available_desc') }}</div>
      <div class="update-card__versions">
        {{ version }} <span class="update-card__arrow">→</span> {{ latestVersion }}
      </div>
      <button class="update-card__btn update-card__btn--primary" @click="goToStore">
        {{ $t('update_now') }}
      </button>
      <button class="update-card__btn update-card__btn--ghost" @click="showUpdateAvailable = false">
        {{ $t('later') }}
      </button>
    </div>
  </v-bottom-sheet>

  <!-- Version label -->
  <div class="version-label">
    <button class="version-label__btn" :class="{ 'version-label__btn--outdated': isOutdated }" @click="onVersionClick">
      <v-icon v-if="isOutdated" size="12">{{ mdiAlertCircleOutline }}</v-icon>
      {{ $t('version') }} {{ version }}
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiAlertCircleOutline, mdiUpdate } from '@mdi/js';
import { quranService } from '@/services/quranService';
import { openUrl } from '@tauri-apps/plugin-opener';

const { locale } = useI18n();

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=org.ilearnquran';

const version        = ref(import.meta.env.VITE_APP_VERSION);
const latestVersion  = ref(null);
const message        = ref(null);
const updateUrl      = ref(null);
const forceUpdate    = ref(false);
const updateAvailable   = ref(false);
const showForceUpdate   = ref(false);
const showUpdateAvailable = ref(false);

const isOutdated = computed(() => forceUpdate.value || updateAvailable.value);

async function checkVersion() {
  try {
    const data = await quranService.getVersion(import.meta.env.VITE_APP_VERSION);
    if (!data) return;
    latestVersion.value  = data.latest_version || data.version;
    message.value        = data.message || null;
    updateUrl.value      = data.update_url || null;
    forceUpdate.value    = data.force_update === true;
    updateAvailable.value = data.update_available === true;
    if (forceUpdate.value)       showForceUpdate.value = true;
    else if (updateAvailable.value) showUpdateAvailable.value = true;
  } catch (error) {
    console.error('Error checking version:', error);
  }
}

function goToStore() {
  openUrl(updateUrl.value || PLAY_STORE_URL);
}

function onVersionClick() {
  if (forceUpdate.value)        showForceUpdate.value = true;
  else if (updateAvailable.value) showUpdateAvailable.value = true;
}

onMounted(checkVersion);
</script>

<style scoped>
/* ── Shared card content ─────────────────────────────── */
.update-card,
.update-sheet {
  background: rgb(var(--v-theme-surface));
  border-radius: 20px;
  padding: 28px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 12px;
}

.update-sheet {
  border-radius: 20px 20px 0 0;
  margin: 0;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 20px);
}

.update-sheet__handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.15);
  margin-bottom: 8px;
  align-self: center;
}

/* ── Icon pill ───────────────────────────────────────── */
.update-card__icon,
.update-sheet__icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.update-card__icon--required {
  background: linear-gradient(135deg, #C62828, #E53935);
  box-shadow: 0 6px 20px rgba(198, 40, 40, 0.35);
}

.update-sheet__icon {
  background: linear-gradient(135deg, var(--clr-primary), #2E7D32);
  box-shadow: 0 6px 20px rgba(27, 94, 32, 0.35);
}

/* ── Text ────────────────────────────────────────────── */
.update-card__title {
  font-family: var(--font-ui);
  font-size: 1.0625rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  text-align: center;
}

.update-card__desc {
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  color: var(--clr-on-surface-muted);
  text-align: center;
  line-height: 1.5;
}

.update-card__versions {
  font-family: var(--font-ui);
  font-size: 0.75rem;
  color: var(--clr-on-surface-muted);
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 20px;
  padding: 4px 14px;
}

.update-card__arrow {
  margin: 0 4px;
  opacity: 0.5;
}

/* ── Buttons ─────────────────────────────────────────── */
.update-card__btn {
  width: 100%;
  padding: 13px 16px;
  border-radius: 14px;
  border: none;
  font-family: var(--font-ui);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 150ms ease;
  margin-top: 2px;
}
.update-card__btn:active { opacity: 0.82; }

.update-card__btn--primary {
  background: var(--clr-primary);
  color: #fff;
}

.update-card__btn--ghost {
  background: transparent;
  color: var(--clr-on-surface-muted);
}

/* ── Version label ───────────────────────────────────── */
.version-label {
  position: absolute;
  bottom: 0;
  left: 1vw;
}

.version-label__btn {
  display: flex;
  align-items: center;
  gap: 2px;
  background: transparent;
  border: none;
  font-size: 0.55rem;
  color: rgba(var(--v-theme-on-surface), 0.35);
  cursor: pointer;
  padding: 2px 4px;
  font-family: var(--font-ui);
}

.version-label__btn--outdated {
  color: rgb(var(--v-theme-error));
}
</style>
